import { Injectable, inject } from '@angular/core';
import { HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod } from '@api-new/authservice';
import { ENTRY_FLOW } from '@app/sign-up/constants/entry-flow';
import { REMORTGAGE_NOW_FLOW } from '@app/sign-up/constants/remotgage-now-flow';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '@shared/models/app-state.model';
import { LocalStorageSyncService } from '@shared/services/local-storage-sync.service';
import { setSignUpFlowCurrentStep } from '@shared/store/sign-up/flow/flow.actions';
import { selectSignUpFlowData } from '@shared/store/sign-up/flow/flow.selectors';
import { SIGN_UP_SMART_SEARCH_SELECTOR_NAME } from '@shared/store/sign-up/smart-search/constants/sign-up-smart-search-selector-name';
import {
  clearSignUpSmartSearchFromStorage,
  loadSignUpSmartSearchFromStorage,
  loadSignUpSmartSearchFromStorageSuccess,
  setSignUpSmartSearchForm,
  setSignUpSmartSearchState,
  skipSignUpSmartSearchCreation,
} from '@shared/store/sign-up/smart-search/smart-search.actions';
import { selectSignUpSmartSearchState } from '@shared/store/sign-up/smart-search/smart-search.selectors';
import { tap, withLatestFrom } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class SignUpSmartSearchEffects {
  private readonly actions$ = inject(Actions);
  private readonly store: Store<AppState> = inject(Store);
  private readonly localStorageSyncService = inject(LocalStorageSyncService);

  loadSmartSearchFromStorage = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSignUpSmartSearchFromStorage),
      map(() =>
        loadSignUpSmartSearchFromStorageSuccess({
          state: this.localStorageSyncService.loadFromStorage(SIGN_UP_SMART_SEARCH_SELECTOR_NAME),
        }),
      ),
    ),
  );

  saveSmartSearchToStorage = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSignUpSmartSearchState, setSignUpSmartSearchForm),
        withLatestFrom(this.store.select(selectSignUpSmartSearchState)),
        tap(([_, smartSearchState]) => {
          this.localStorageSyncService.saveToStorage(SIGN_UP_SMART_SEARCH_SELECTOR_NAME, smartSearchState);
        }),
      ),
    { dispatch: false },
  );

  clearSignUpSmartSearchFromStorage = createEffect(
    () =>
      this.actions$.pipe(
        ofType(clearSignUpSmartSearchFromStorage),
        tap(() => {
          this.localStorageSyncService.deleteFromStorage(SIGN_UP_SMART_SEARCH_SELECTOR_NAME);
        }),
      ),
    { dispatch: false },
  );

  skipMortgageCreation = createEffect(() =>
    this.actions$.pipe(
      ofType(skipSignUpSmartSearchCreation),
      withLatestFrom(this.store.select(selectSignUpFlowData)),
      map(([_, flowData]) => {
        switch (flowData.creationMethod) {
          case HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod.SIGN_UP_CREATION_METHOD_REMORTGAGE_NOW:
            return setSignUpFlowCurrentStep({ step: REMORTGAGE_NOW_FLOW.UserCreate });
        }

        return setSignUpFlowCurrentStep({ step: ENTRY_FLOW.Landing });
      }),
    ),
  );
}
